import React, { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";

/**
 * Reusable "Install as App" button for PWA.
 * - Dynamically injects the right manifest tag (admin vs customer).
 * - Registers /sw-driver.js if available.
 * - Shows a button when beforeinstallprompt fires (Chrome/Edge/Android).
 * - Shows iOS hint card if iOS Safari (no PWA install API).
 *
 * Props:
 *   scope: "admin" | "customer"
 *   variant: "button" (default) | "compact"
 */
export default function PWAInstallButton({ scope = "customer", variant = "button" }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const manifestHref = scope === "admin" ? "/manifest-admin.json" : "/manifest-customer.json";
    const attr = `data-pwa-${scope}`;
    let link = document.querySelector(`link[rel="manifest"][${attr}]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.href = manifestHref;
      link.setAttribute(attr, "true");
      document.head.appendChild(link);
    }
    // Register service worker (best-effort; ignore on iOS to avoid PWA caching issues)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw-driver.js").catch(() => {});
    }
    // Detect if already running as installed PWA
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) {
      setInstalled(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Auto-show banner once per scope per user (skip if previously dismissed/accepted)
      const key = `mv_pwa_banner_dismissed_${scope}`;
      if (!localStorage.getItem(key)) {
        setTimeout(() => setShowBanner(true), 4000);
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setShowBanner(false);
      localStorage.setItem(`mv_pwa_banner_dismissed_${scope}`, "installed");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: no beforeinstallprompt → show banner once with manual instructions
    if (isIOS && !isStandalone) {
      const key = `mv_pwa_banner_dismissed_${scope}`;
      if (!localStorage.getItem(key)) {
        setTimeout(() => setShowBanner(true), 5000);
      }
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [scope]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem(`mv_pwa_banner_dismissed_${scope}`, "1");
  };

  const handleInstall = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setInstallPrompt(null);
      setShowBanner(false);
      localStorage.setItem(`mv_pwa_banner_dismissed_${scope}`, outcome);
      return;
    }
    if (isIOS) {
      setIosHint(true);
      return;
    }
    // Fallback: cross-browser manual instructions
    setIosHint(true);
  };

  if (installed) return null;

  const appName = scope === "admin" ? "MV Admin" : "MV Cliente";

  const banner = showBanner ? (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] bg-[#09090B] text-white shadow-2xl rounded-sm border border-zinc-800 w-[min(95vw,480px)] p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-5 duration-500"
      data-testid={`pwa-install-banner-${scope}`}
      role="dialog"
      aria-label="Installa app"
    >
      <div className="w-10 h-10 rounded-sm bg-[#FF5200] flex items-center justify-center flex-shrink-0">
        <Smartphone size={20} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Installa l'app</p>
        <p className="text-sm font-semibold mt-0.5">Apri {appName} dalla schermata principale, senza browser</p>
        <p className="text-[12px] text-zinc-400 mt-1">Accesso più rapido, notifiche e funziona anche offline.</p>
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={handleInstall}
            className="inline-flex items-center gap-1.5 h-9 px-4 text-[12px] font-bold uppercase tracking-wide rounded-sm bg-[#FF5200] text-white hover:bg-[#e64a00] transition-colors"
            data-testid={`pwa-banner-install-${scope}`}
          >
            <Download size={13} /> Installa ora
          </button>
          <button
            type="button"
            onClick={dismissBanner}
            className="h-9 px-3 text-[11px] font-bold uppercase tracking-wide text-zinc-400 hover:text-white"
            data-testid={`pwa-banner-dismiss-${scope}`}
          >
            Più tardi
          </button>
        </div>
      </div>
      <button onClick={dismissBanner} className="text-zinc-500 hover:text-white" aria-label="Chiudi" data-testid={`pwa-banner-close-${scope}`}><X size={16} /></button>
    </div>
  ) : null;

  // No prompt available on iOS or unsupported browsers → still show button but with hint
  if (variant === "compact") {
    return (
      <>
        <button
          type="button"
          onClick={handleInstall}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 text-[11px] font-bold uppercase tracking-wide rounded-sm border border-zinc-200 bg-white text-[#09090B] hover:border-[#FF5200] hover:text-[#FF5200] transition-colors"
          data-testid={`pwa-install-btn-${scope}`}
          title="Installa MV Trasporti come app"
        >
          <Download size={12} /> Installa app
        </button>
        {iosHint && <PWAInstallHint scope={scope} onClose={() => setIosHint(false)} />}
        {banner}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        className="inline-flex items-center gap-2 h-10 px-4 text-xs font-bold uppercase tracking-wide rounded-sm bg-[#FF5200] text-white hover:bg-[#e64a00] transition-colors"
        data-testid={`pwa-install-btn-${scope}`}
        title="Installa MV Trasporti come app"
      >
        <Download size={14} /> Installa come app
      </button>
      {iosHint && <PWAInstallHint scope={scope} onClose={() => setIosHint(false)} />}
      {banner}
    </>
  );
}

function PWAInstallHint({ scope, onClose }) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const appName = scope === "admin" ? "MV Admin" : "MV Cliente";
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full p-6 rounded-sm border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Smartphone size={20} className="text-[#FF5200]" />
            <h3 className="font-heading text-lg font-bold uppercase">Installa {appName}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="pwa-hint-close"><X size={18} /></button>
        </div>
        {isIOS ? (
          <div className="space-y-3 text-sm text-zinc-700">
            <p className="font-semibold">Su iPhone/iPad (Safari):</p>
            <ol className="list-decimal pl-5 space-y-1.5 text-[13px]">
              <li>Tocca l'icona <b>Condividi</b> in basso (un quadrato con freccia in su)</li>
              <li>Scorri e tocca <b>"Aggiungi a Home"</b></li>
              <li>Conferma con <b>"Aggiungi"</b></li>
            </ol>
            <p className="text-[12px] text-zinc-500 mt-3">L'app comparirà sulla schermata Home come un'icona normale.</p>
          </div>
        ) : (
          <div className="space-y-3 text-sm text-zinc-700">
            <p className="font-semibold">Per installare {appName}:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-[13px]">
              <li><b>Chrome/Edge (desktop)</b>: clicca sull'icona di installazione nella barra dell'indirizzo (a destra), oppure menu ⋮ → "Installa app"</li>
              <li><b>Android Chrome</b>: menu ⋮ → "Aggiungi a schermata Home" o "Installa app"</li>
              <li><b>Firefox</b>: l'opzione di installazione non è disponibile</li>
            </ul>
            <p className="text-[12px] text-zinc-500 mt-3">Una volta installata, l'app si apre come una finestra dedicata, senza la barra del browser.</p>
          </div>
        )}
      </div>
    </div>
  );
}
